import { fieldServiceKind, type FieldServiceKind } from './operatorDeskServiceCatalog';
/**
 * Operator Desk company-map work items (Bid / RFP / Now) keyed to an API well.
 * Status color is shared by the map pins and the well-details Work card.
 */
import { normalizeOperatorDeskPolygon } from './operatorDeskGeo';

export const OPERATOR_DESK_STATUS_COLORS = {
  posted: '#38bdf8',
  booked: '#a78bfa',
  progress: '#fbbf24',
  complete: '#22c55e',
  cancelled: '#f87171',
} as const;

export type OperatorDeskWorkStatus = keyof typeof OPERATOR_DESK_STATUS_COLORS;

export const OPERATOR_DESK_STATUS_LABELS: Record<OperatorDeskWorkStatus, string> = {
  posted: 'Requested',
  booked: 'Booked',
  progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

export type OperatorDeskWorkSource = 'bid' | 'guest' | 'water' | 'wm' | 'welder' | 'haul' | 'porta';

/** Geofence colors on the Operator Desk globe. */
export const OPERATOR_DESK_DOME_COLORS = {
  rfp: '#38bdf8',
  bid: '#fbbf24',
  request: '#ef4444',
} as const;

export type OperatorDeskDomeKind = keyof typeof OPERATOR_DESK_DOME_COLORS;

export function operatorDeskDomeKind(listingKind: unknown, source?: unknown): OperatorDeskDomeKind {
  const kind = String(listingKind || '').trim().toLowerCase();
  if (kind === 'rfp') return 'rfp';
  if (kind === 'funded_bid' || kind === 'bid') return 'bid';
  const src = String(source || '').trim().toLowerCase();
  if (src === 'bid' && kind !== 'now' && kind !== 'customer_offer') return 'bid';
  return 'request';
}

export function operatorDeskDomeColor(listingKind: unknown, source?: unknown): string {
  return OPERATOR_DESK_DOME_COLORS[operatorDeskDomeKind(listingKind, source)];
}

export type OperatorDeskWorkUpdate = {
  id: string;
  note: string | null;
  status: string | null;
  authorName: string | null;
  createdAt: string;
};

export const PAD_WORK_FIELD_KINDS = ['text', 'yes_no', 'number', 'before_after', 'equipment', 'photo'] as const;
export type PadWorkFieldKind = typeof PAD_WORK_FIELD_KINDS[number];

export type PadWorkFieldDraft = {
  kind: PadWorkFieldKind;
  prompt: string;
  unit?: string;
  equipmentLabel?: string;
  knownValue?: string;
};

export type PadWorkFieldChip = {
  id: string;
  label: string;
  field: PadWorkFieldDraft;
};

export type OperatorDeskWorkQuestion = {
  id: string;
  prompt: string;
  sortIndex: number;
  kind: PadWorkFieldKind;
  unit: string | null;
  equipmentLabel: string | null;
  knownValue: string | null;
  answer: {
    id: string;
    text: string;
    authorName: string | null;
    createdAt: string;
    number: number | null;
    after: number | null;
    yes: boolean | null;
    photoUrl: string | null;
  } | null;
};

export function isPadWorkFieldKind(value: unknown): value is PadWorkFieldKind {
  return PAD_WORK_FIELD_KINDS.includes(String(value || '') as PadWorkFieldKind);
}

export function normalizePadWorkFields(input: unknown): PadWorkFieldDraft[] {
  if (!Array.isArray(input)) return [];
  const out: PadWorkFieldDraft[] = [];
  for (const raw of input) {
    if (typeof raw === 'string' || typeof raw === 'number') {
      const prompt = String(raw || '').trim().slice(0, 500);
      if (!prompt) continue;
      out.push({ kind: 'text', prompt });
    } else if (raw && typeof raw === 'object') {
      const row = raw as Record<string, unknown>;
      const kind = isPadWorkFieldKind(row.kind) ? row.kind : 'text';
      const equipmentLabel = String(row.equipmentLabel || row.equipment_label || '').trim().slice(0, 120);
      const prompt = String(row.prompt || equipmentLabel).trim().slice(0, 500);
      const unit = String(row.unit || '').trim().slice(0, 24);
      const knownValue = String(row.knownValue || row.known_value || '').trim().slice(0, 80);
      if (kind === 'equipment') {
        if (!equipmentLabel && !prompt) continue;
        out.push({
          kind,
          prompt: prompt || equipmentLabel,
          equipmentLabel: equipmentLabel || prompt,
          unit: unit || 'hrs',
          knownValue: knownValue || undefined,
        });
      } else {
        if (!prompt) continue;
        out.push({
          kind,
          prompt,
          unit: (kind === 'number' || kind === 'before_after') ? (unit || undefined) : undefined,
        });
      }
    }
    if (out.length >= 20) break;
  }
  return out;
}

export function mapPadWorkAnswerRow(row: {
  id: string;
  author_name?: string | null;
  answer_text?: string | null;
  created_at?: string | null;
  answer_number?: number | null;
  answer_after?: number | null;
  answer_yes?: number | boolean | null;
  photo_url?: string | null;
}): NonNullable<OperatorDeskWorkQuestion['answer']> {
  const yesRaw = row.answer_yes;
  return {
    id: row.id,
    text: String(row.answer_text || ''),
    authorName: row.author_name || null,
    createdAt: String(row.created_at || ''),
    number: row.answer_number != null && Number.isFinite(Number(row.answer_number)) ? Number(row.answer_number) : null,
    after: row.answer_after != null && Number.isFinite(Number(row.answer_after)) ? Number(row.answer_after) : null,
    yes: yesRaw == null ? null : Boolean(Number(yesRaw)),
    photoUrl: row.photo_url ? String(row.photo_url) : null,
  };
}

export function mapPadWorkQuestionRow(row: {
  id: string;
  prompt: string;
  sort_index?: number;
  kind?: string | null;
  unit?: string | null;
  equipment_label?: string | null;
  known_value?: string | null;
  answer?: OperatorDeskWorkQuestion['answer'];
}): OperatorDeskWorkQuestion {
  return {
    id: row.id,
    prompt: row.prompt,
    sortIndex: Number(row.sort_index || 0),
    kind: isPadWorkFieldKind(row.kind) ? row.kind : 'text',
    unit: row.unit ? String(row.unit) : null,
    equipmentLabel: row.equipment_label ? String(row.equipment_label) : null,
    knownValue: row.known_value ? String(row.known_value) : null,
    answer: row.answer || null,
  };
}

export function formatPadWorkAnswerText(input: {
  kind?: string | null;
  unit?: string | null;
  text?: string | null;
  number?: number | null;
  after?: number | null;
  yes?: boolean | null;
}): string {
  const unit = input.unit ? ` ${input.unit}` : '';
  if (input.kind === 'yes_no' && input.yes != null) {
    return input.yes ? 'Yes' : 'No';
  }
  if (input.kind === 'before_after' && (input.number != null || input.after != null)) {
    const a = input.number != null ? String(input.number) : '—';
    const b = input.after != null ? String(input.after) : '—';
    return `${a}${unit} → ${b}${unit}`.trim();
  }
  if ((input.kind === 'number' || input.kind === 'equipment') && input.number != null) {
    return `${input.number}${unit}`.trim();
  }
  return String(input.text || '').trim();
}

export function padWorkQuestionLabel(question: {
  prompt: string;
  kind?: string | null;
  unit?: string | null;
  equipmentLabel?: string | null;
  knownValue?: string | null;
}): string {
  if (question.kind === 'equipment') {
    const name = question.equipmentLabel || question.prompt;
    const last = question.knownValue
      ? `last ${question.knownValue}${question.unit ? ` ${question.unit}` : ''}`
      : null;
    return last ? `${name} · ${last}` : name;
  }
  return question.prompt;
}

export type OperatorDeskWorkItem = {
  welderJourney?: import('./welderWork').WelderJobJourney | null;
  haulJourney?: import('./hotshotJourney').HotshotJourney | null;
  waterJourney?: import('./waterJourney').WaterDeliveryJourney | null;
  wmJourney?: import('./wmJourney').WmDeskJourney | null;
  portaJourney?: import('./portaJourney').PortaDeskJourney | null;
  /** Creator / company Desk member may Request pump while dropped_off. */
  canSchedulePump?: boolean;
  schedulePumpDisabledReason?: string | null;
  /** Creator / company Desk member may Edit due on open scheduled pump visits. */
  canEditPumpDue?: boolean;
  editPumpDueDisabledReason?: string | null;
  /** Creator / company Desk member may Cancel one open scheduled pump visit. */
  canCancelPumpVisit?: boolean;
  cancelPumpVisitDisabledReason?: string | null;
  /** Creator / company job-master may Cancel the whole porta job / rental. */
  canCancelJob?: boolean;
  cancelJobDisabledReason?: string | null;
  canRetryCancelRefund?: boolean;
  retryCancelRefundDisabledReason?: string | null;
  cancelMoneyAction?: string | null;
  cancelRefundStatus?: string | null;
  /** Creator / job-master may Unassign claimed driver (pre-delivery → accepted). */
  canUnassignPorta?: boolean;
  /** Creator / job-master may Reassign preferred / claimed driver mid-run. */
  canReassignPorta?: boolean;
  foodJourney?: import('./foodJourney').FoodDeliveryJourney | null;
  proofPhotos?: import('./operatorDeskWm').OperatorDeskProofPhoto[];
  /** True when the signed-in Operator Desk user is the one who requested this work. */
  requestedByMe?: boolean;
  id: string;
  source: OperatorDeskWorkSource;
  sourceId: string;
  listingKind?: string | null;
  service: string;
  title: string;
  /** Operator-typed project name from the request. Used by the Projects tab. */
  projectName?: string | null;
  projectId?: string | null;
  county?: string | null;
  scheduledAt?: string | null;
  bidClosesAt?: string | null;
  status: OperatorDeskWorkStatus;
  rawStatus?: string;
  statusLabel: string;
  statusColor: string;
  apiNumber: string | null;
  publicNumber: string | null;
  siteType?: string | null;
  facilityName: string | null;
  operator: string | null;
  contractor: string | null;
  contractorId: string | null;
  lat: number | null;
  lng: number | null;
  createdAt: string | null;
  updates: OperatorDeskWorkUpdate[];
  questions: OperatorDeskWorkQuestion[];
  canUpdate: boolean;
  canAnswer: boolean;
  canPingLocation: boolean;
  /** Bid at an API well stamped with the JSA completion gate (and not waived). */
  jsaRequired?: boolean;
  /** The awarded contractor already has a signed JSA matching this well. */
  jsaSigned?: boolean;
  /** Pin-the-work compass heading (north / east / …) for the Map globe 3D icon. */
  orientation?: string | null;
  /** Pin-the-work 3D kind: dumpster / water / grocery / tacos / hotshot / welder / semi. */
  serviceKind?: OperatorDeskPinKind | null;
  /** Operator-drawn work-area square, if they set one on Pin the work. */
  workArea?: Array<{ lat: number; lng: number }> | null;
  /** Operator archived this titled project to History. */
  archived?: boolean;
  /** Server-confirmed customer payment/reserve, returned only for scoped operator lists. */
  paymentConfirmed?: boolean;
  /**
   * Haul / water / WM (or bid-award haul) has payment_hold_at set — charge disputed/refunded.
   * Distinct from normal water_hold/waste_hold escrow ("In escrow"). Desk Payouts
   * surfaces this as the Held chip; Approve cannot release until cleared.
   */
  paymentHold?: boolean;
  /**
   * Known order/award amount in cents when the vertical already stored one
   * (haul/guest amount_cents, water job_price_cents, wm amount_cents, bid awarded_amount).
   * Null/omitted means unknown — never invent $0 for display.
   */
  amountCents?: number | null;
};

export function operatorDeskHistoryKind(listingKind: unknown, source?: unknown): 'rfps' | 'bid' | 'now' {
  const kind = String(listingKind || '').trim().toLowerCase();
  if (kind === 'rfp') return 'rfps';
  if (kind === 'funded_bid' || kind === 'bid') return 'bid';
  const src = String(source || '').trim().toLowerCase();
  if (src === 'bid' && kind !== 'now' && kind !== 'customer_offer') return 'bid';
  return 'now';
}

export type OperatorDeskPinKind =
  | FieldServiceKind
  | 'dumpster'
  | 'porta'
  | 'water'
  | 'grocery'
  | 'tacos'
  | 'hotshot'
  | 'welder'
  | 'semi';

const DESK_ORIENTATION_ALIASES: Record<string, string> = {
  north: 'north',
  n: 'north',
  '0': 'north',
  northeast: 'northeast',
  ne: 'northeast',
  '45': 'northeast',
  east: 'east',
  e: 'east',
  '90': 'east',
  southeast: 'southeast',
  se: 'southeast',
  '135': 'southeast',
  south: 'south',
  s: 'south',
  '180': 'south',
  southwest: 'southwest',
  sw: 'southwest',
  '225': 'southwest',
  west: 'west',
  w: 'west',
  '270': 'west',
  northwest: 'northwest',
  nw: 'northwest',
  '315': 'northwest',
};

/** Compass heading stored from Pin the work. */
export function parseOperatorDeskOrientation(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const deg = ((Math.round(Number(raw) / 45) * 45) % 360 + 360) % 360;
    return DESK_ORIENTATION_ALIASES[String(deg)] || null;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return parseOperatorDeskOrientation(JSON.parse(t));
      } catch {
        /* plain string */
      }
    }
    const spaced = t.toLowerCase();
    const compact = spaced.replace(/[\s_-]+/g, '');
    return DESK_ORIENTATION_ALIASES[spaced] || DESK_ORIENTATION_ALIASES[compact] || null;
  }
  if (Array.isArray(raw)) {
    for (const row of raw) {
      const hit = parseOperatorDeskOrientation(row);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const key of [
      'orientation',
      'pinOrientation',
      'pin_orientation',
      'requested_orientation',
      'requestedOrientation',
    ]) {
      const hit = parseOperatorDeskOrientation(o[key]);
      if (hit) return hit;
    }
    if (o.pin && typeof o.pin === 'object') {
      const hit = parseOperatorDeskOrientation((o.pin as Record<string, unknown>).orientation);
      if (hit) return hit;
    }
  }
  return null;
}

/** Work-area polygon from Pin the work / sites_json / work_area_json. */
export function parseOperatorDeskWorkArea(raw: unknown): Array<{ lat: number; lng: number }> | null {
  const fromGeo = normalizeOperatorDeskPolygon(raw);
  if (fromGeo) return fromGeo;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return parseOperatorDeskWorkArea(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return null;
  }
  if (Array.isArray(raw)) {
    const asPoly = normalizeOperatorDeskPolygon(raw);
    if (asPoly) return asPoly;
    for (const row of raw) {
      const hit = parseOperatorDeskWorkArea(row);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const key of ['workArea', 'work_area', 'workAreaJson', 'work_area_json', 'polygon']) {
      const hit = parseOperatorDeskWorkArea(o[key]);
      if (hit) return hit;
    }
  }
  return null;
}

const PIN_KIND_ALIASES: Record<string, OperatorDeskPinKind> = {
  dumpster: 'dumpster',
  dumpsters: 'dumpster',
  wm: 'dumpster',
  waste: 'dumpster',
  waste_management: 'dumpster',
  trash: 'dumpster',
  porta: 'porta',
  porta_potty: 'porta',
  portapotty: 'porta',
  portable_toilet: 'porta',
  water: 'water',
  water_delivery: 'water',
  waterhaul: 'water',
  water_haul: 'water',
  grocery: 'grocery',
  groceries: 'grocery',
  tacos: 'tacos',
  taco: 'tacos',
  food: 'tacos',
  catering: 'tacos',
  tacos_deliver: 'tacos',
  hotshot: 'hotshot',
  hotshots: 'hotshot',
  hot_shot: 'hotshot',
  welder: 'welder',
  welding: 'welder',
  semi: 'semi',
  semis: 'semi',
  trucking: 'semi',
};

/** Map a desk work service label / bid category onto a Pin-the-work 3D kind. */
export function operatorDeskWorkServiceKind(service: unknown): OperatorDeskPinKind | null {
  const key = String(service || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (!key) return null;
  return fieldServiceKind(key) || PIN_KIND_ALIASES[key] || PIN_KIND_ALIASES[key.replace(/_/g, '')] || null;
}

export function operatorDeskPinKindFromUnknown(raw: unknown): OperatorDeskPinKind | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' && (raw.startsWith('{') || raw.startsWith('['))) {
    try {
      return operatorDeskPinKindFromUnknown(JSON.parse(raw));
    } catch {
      return operatorDeskWorkServiceKind(raw);
    }
  }
  if (Array.isArray(raw)) {
    for (const row of raw) {
      const hit = operatorDeskPinKindFromUnknown(row);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    return operatorDeskWorkServiceKind(o.serviceKind || o.service_kind || o.service)
      || operatorDeskPinKindFromUnknown(o.pin);
  }
  return operatorDeskWorkServiceKind(raw);
}

export function operatorDeskWorkStatusColor(status: OperatorDeskWorkStatus): string {
  return OPERATOR_DESK_STATUS_COLORS[status];
}

export function operatorDeskApiDigits(raw: unknown): string {
  return String(raw || '').replace(/\D/g, '').slice(0, 10);
}

const GENERIC_FACILITY_LABELS = new Set([
  'site',
  'dumpster',
  'work site',
  'pad',
  'drop site',
  'request',
  'requests',
  'now',
  'haul',
  'water',
  'weld',
  'rfp',
  'rfps',
  'bid',
  'bids',
  'work',
  'wm',
  'porta',
  'porta potty',
]);

/** Real pad / facility name — never the generic “Site” placeholder. */
export function operatorDeskFacilityLabel(raw: unknown): string | null {
  const value = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!value) return null;
  const head = value.split(/\s*·\s*/)[0]?.trim() || value;
  if (/^API\b/i.test(head)) return null;
  if (GENERIC_FACILITY_LABELS.has(head.toLowerCase())) {
    const rest = value.slice(head.length).replace(/^\s*·\s*/, '').trim();
    if (rest && !GENERIC_FACILITY_LABELS.has(rest.toLowerCase()) && !/^API\b/i.test(rest)) {
      return rest;
    }
    return null;
  }
  return value;
}

export function parseOperatorDeskNotesApi(notes: unknown): string | null {
  const text = String(notes || '');
  const tagged = text.match(/API\s*#?\s*[:#]?\s*([\d-]{8,})/i);
  if (tagged?.[1] && operatorDeskApiDigits(tagged[1]).length >= 8) {
    return tagged[1].trim();
  }
  return null;
}

export function parseOperatorDeskNotesFacility(notes: unknown): string | null {
  const text = String(notes || '');
  const facility = text.match(/^Facility\s+(.+)$/im);
  if (facility?.[1]) return operatorDeskFacilityLabel(facility[1]);
  return null;
}

export function operatorDeskApiDigitsMatch(a: unknown, b: unknown): boolean {
  const x = operatorDeskApiDigits(a);
  const y = operatorDeskApiDigits(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar day YYYY-MM-DD for well-file “what happened Tuesday.” */
export function wellFileLocalDayKey(raw: unknown): string | null {
  const s = String(raw || '').trim();
  if (!s) return null;
  const iso = /T/.test(s) ? s : `${s.replace(' ', 'T')}${s.endsWith('Z') ? '' : 'Z'}`;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function wellFileTodayKey(now = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

export function operatorDeskWorkOnDay(
  item: Pick<OperatorDeskWorkItem, 'createdAt' | 'updates' | 'questions'>,
  day: string | null,
): boolean {
  if (!day) return true;
  const stamps: unknown[] = [item.createdAt];
  for (const update of item.updates || []) stamps.push(update.createdAt);
  for (const question of item.questions || []) {
    if (question.answer?.createdAt) stamps.push(question.answer.createdAt);
  }
  return stamps.some((stamp) => wellFileLocalDayKey(stamp) === day);
}

export function operatorDeskWorkStatus(raw: unknown): OperatorDeskWorkStatus {
  const s = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!s) return 'posted';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (
    s === 'completed'
    || s === 'complete'
    || s === 'delivered'
    || s === 'picked_up'
    || s === 'dropped_off'
  ) {
    return 'complete';
  }
  if (
    s === 'awaiting_confirm'
    || s === 'en_route'
    || s === 'en_route_dropoff'
    || s === 'en_route_pickup'
    || s === 'arrived'
    || s === 'in_progress'
    || s === 'dispatched'
  ) {
    return 'progress';
  }
  if (
    s === 'awarded'
    || s === 'claimed'
    || s === 'accepted'
    || s === 'assigned'
    || s === 'paid'
    || s === 'booked'
  ) {
    return 'booked';
  }
  if (s === 'open' || s === 'requested' || s === 'pending' || s === 'pending_pay' || s === 'draft' || s === 'awaiting_payment') {
    return 'posted';
  }
  return 'posted';
}

export function operatorDeskWorkItemId(source: OperatorDeskWorkSource, sourceId: string): string {
  return `${source}:${sourceId}`;
}

export function parseOperatorDeskWorkItemId(id: string): { source: OperatorDeskWorkSource; sourceId: string } | null {
  let trimmed = String(id || '').trim();
  try {
    trimmed = decodeURIComponent(trimmed);
  } catch {
    /* keep raw — malformed % sequences */
  }
  const idx = trimmed.indexOf(':');
  if (idx <= 0) return null;
  const source = trimmed.slice(0, idx) as OperatorDeskWorkSource;
  const sourceId = trimmed.slice(idx + 1).trim();
  if (!sourceId) return null;
  if (source !== 'bid' && source !== 'guest' && source !== 'water' && source !== 'wm' && source !== 'welder' && source !== 'haul' && source !== 'porta') {
    return null;
  }
  return { source, sourceId };
}

export function operatorDeskServiceLabel(raw: unknown): string {
  const s = String(raw || '').trim();
  if (!s) return 'Service';
  const key = s
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (key === 'rfp' || key === 'funded_bid') return 'RFP';
  if (key === 'waste_management' || key === 'dumpster' || key === 'dumpsters' || key === 'wm') return 'WM';
  if (key === 'hotshots' || key === 'hotshot' || key === 'hot_shot') return 'Hotshot';
  if (key === 'semis' || key === 'semi') return 'Semi';
  if (key === 'water' || key === 'water_delivery') return 'Water';
  if (key === 'grocery' || key === 'groceries') return 'Grocery';
  if (key === 'tacos' || key === 'tacos_deliver' || key === 'food') return 'Tacos';
  if (key === 'porta' || key === 'porta_potty' || key === 'portapotty') return 'Porta';
  if (key === 'welding' || key === 'welder') return 'Welding';
  return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const OPERATOR_DESK_LEGAL_TOKENS = new Set([
  'LP',
  'LLP',
  'LLC',
  'INC',
  'INCORPORATED',
  'CO',
  'CORP',
  'CORPORATION',
  'COMPANY',
  'COMPANIES',
  'LTD',
  'LIMITED',
  'PLC',
  'USA',
  'US',
  'THE',
  'OF',
  'AND',
  'NA',
]);

const OPERATOR_DESK_BRAND_ALIASES: Record<string, string> = {
  OXY: 'OCCIDENTAL',
};

/** Collapse legal-entity suffixes so CHEVRON OIL COMPANY and CHEVRON PIPE LINE share one lens. */
export function operatorDeskBrandKey(raw: unknown): string {
  const upper = String(raw || '')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
  if (!upper) return '';
  const tokens = upper.split(/\s+/).filter((token) => token && !OPERATOR_DESK_LEGAL_TOKENS.has(token));
  const use = tokens.length ? tokens : upper.split(/\s+/).filter(Boolean);
  if (!use.length) return upper;
  const first = use[0];
  const key = first.length <= 3 && use[1] ? `${first} ${use[1]}` : first;
  return OPERATOR_DESK_BRAND_ALIASES[first] || OPERATOR_DESK_BRAND_ALIASES[key] || key;
}

/** LIKE tokens so OXY USA wells still match an Occidental company seat. */
export function operatorDeskBrandSearchTokens(raw: unknown): string[] {
  const key = operatorDeskBrandKey(raw);
  if (!key) return [];
  const tokens = new Set<string>([key]);
  for (const [alias, brand] of Object.entries(OPERATOR_DESK_BRAND_ALIASES)) {
    if (brand === key) tokens.add(alias);
  }
  return [...tokens];
}

export function groupOperatorDeskBrands(names: string[]): string[] {
  const seen = new Set<string>();
  const brands: string[] = [];
  for (const name of names) {
    const key = operatorDeskBrandKey(name);
    if (!key || seen.has(key)) continue;
    if (key.replace(/\s+/g, '').length < 3) continue;
    seen.add(key);
    brands.push(key);
  }
  return brands.sort((a, b) => a.localeCompare(b));
}

/** Typeahead: brand contains the query. Never match one-letter brands inside "chevron". */
export function operatorDeskBrandMatchesQuery(brand: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  const n = String(brand || '').trim().toLowerCase();
  if (q.length < 2 || n.length < 3) return false;
  if (n.startsWith(q) || n.includes(` ${q}`)) return true;
  if (q.length >= 3 && n.includes(q)) return true;
  const keyed = operatorDeskBrandKey(query).toLowerCase();
  return keyed.length >= 3 && keyed === n;
}
